import { useCallback, useEffect, useMemo, useState } from 'react';

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Localization,
  type MRT_PaginationState,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { convertNumber, convertValue, toCurrency } from 'src/utils/number';

import { MovementTypeChip } from './MovementTypeChip';
import { SummaryCards } from './SummaryCards';
import { ALL_PERIOD_SELECTION } from './mapChildrenToFundMovements';

import type {
  FundMovement,
  FundMovementsPeriodSelection,
  FundMovementsResponse,
  FundMovementsSummary,
} from './types';
import type { SelectChangeEvent } from '@mui/material';

export interface FundMovementsDetailedViewProps {
  data: FundMovementsResponse | null;
  selectedPeriod: FundMovementsPeriodSelection;
  availableCompetences: string[];
  onPeriodChange: (period: FundMovementsPeriodSelection) => void;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
}

const EMPTY_SUMMARY_ITEM = { totalValue: 0, shareQuantity: 0 };

const EMPTY_SUMMARY: FundMovementsSummary = {
  purchases: EMPTY_SUMMARY_ITEM,
  sales: EMPTY_SUMMARY_ITEM,
  net: EMPTY_SUMMARY_ITEM,
};

const TABLE_LOCALIZATION: MRT_Localization = {
  ...MRT_Localization_PT_BR,
  noRecordsToDisplay: 'Não há dados disponíveis no período selecionado.',
};

function formatMovementDate(isoDate: string): string {
  try {
    return format(parseISO(isoDate), 'dd/MM/yyyy');
  } catch {
    return isoDate;
  }
}

function formatCompetenceOption(isoDate: string): string {
  try {
    const label = format(parseISO(isoDate), 'MMMM/yyyy', { locale: ptBR });
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch {
    return isoDate;
  }
}

function formatNetValue(value: number): string {
  if (value < 0) {
    return `- ${convertValue(Math.abs(value))}`;
  }
  return convertValue(value);
}

function exportMovementsCsv(data: FundMovementsResponse): void {
  const header = [
    'Data',
    'Tipo',
    'Nome do fundo',
    'Quantidade de cotas',
    'Valor da cota',
    'Valor líquido',
  ];

  const rows = data.movements.map((movement) => [
    formatMovementDate(movement.date),
    movement.type === 'compra' ? 'Compra' : 'Venda',
    movement.fundName,
    String(movement.shareQuantity),
    String(movement.sharePrice),
    String(movement.netValue),
  ]);

  const csvContent = [header, ...rows]
    .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(';'))
    .join('\n');

  const blob = new Blob([`\uFEFF${csvContent}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `movimentacoes-${data.referenceMonth}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export function FundMovementsDetailedView({
  data,
  selectedPeriod,
  availableCompetences,
  onPeriodChange,
  isLoading = false,
  isError = false,
  errorMessage = 'Não foi possível carregar as movimentações.',
}: FundMovementsDetailedViewProps) {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const columns = useMemo<MRT_ColumnDef<FundMovement>[]>(
    () => [
      {
        accessorKey: 'date',
        header: 'Data',
        size: 120,
        Cell: ({ cell }) => formatMovementDate(String(cell.getValue() ?? '')),
      },
      {
        accessorKey: 'type',
        header: 'Tipo',
        size: 120,
        Cell: ({ cell }) => (
          <MovementTypeChip
            type={cell.getValue<'compra' | 'venda'>() ?? 'compra'}
          />
        ),
      },
      {
        accessorKey: 'fundName',
        header: 'Nome do fundo',
        size: 200,
        Cell: ({ row }) => {
          const { fundName, fundSlug, fundOperationId } = row.original;
          if (!fundName) return '-';
          if (!fundSlug || !fundOperationId) return fundName;
          return (
            <Link
              href={`/operacao/fidc/${fundSlug}/${fundOperationId}`}
              target="_blank"
              rel="noopener noreferrer">
              {fundName}
            </Link>
          );
        },
      },
      {
        accessorKey: 'shareQuantity',
        header: 'Quantidade de cotas',
        size: 160,
        Cell: ({ cell }) => convertNumber(Number(cell.getValue() ?? 0)),
      },
      {
        accessorKey: 'sharePrice',
        header: 'Valor da cota',
        size: 140,
        Cell: ({ cell }) => toCurrency(Number(cell.getValue() ?? 0)),
      },
      {
        accessorKey: 'netValue',
        header: 'Valor líquido',
        size: 160,
        Header: () => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>Valor líquido</span>
            <Tooltip title="Valor líquido da movimentação (compras positivas, vendas negativas)">
              <InfoOutlinedIcon
                fontSize="inherit"
                sx={{ color: 'text.secondary', fontSize: 16 }}
                aria-label="Informação sobre valor líquido"
              />
            </Tooltip>
          </Box>
        ),
        Cell: ({ cell }) => {
          const value = Number(cell.getValue() ?? 0);
          return (
            <Typography
              component="span"
              variant="body2"
              sx={{
                color: value < 0 ? 'error.main' : 'success.main',
                fontWeight: 600,
              }}>
              {formatNetValue(value)}
            </Typography>
          );
        },
      },
    ],
    [],
  );

  const movements = data?.movements ?? [];
  const summary = data?.summary ?? EMPTY_SUMMARY;

  const renderEmptyRowsFallback = useCallback(
    () => (
      <Typography
        color="text.secondary"
        sx={{ py: 4, textAlign: 'center', width: '100%' }}>
        Não há dados disponíveis no período selecionado.
      </Typography>
    ),
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: movements,
    localization: TABLE_LOCALIZATION,
    enableTopToolbar: false,
    enableBottomToolbar: true,
    enableColumnActions: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableSorting: true,
    enablePagination: true,
    initialState: {
      sorting: [{ id: 'date', desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 },
    },
    state: { pagination, isLoading },
    onPaginationChange: setPagination,
    muiTableContainerProps: { sx: { maxHeight: 360 } },
    muiTablePaperProps: { elevation: 0, sx: { border: 'none' } },
    renderEmptyRowsFallback,
  });

  useEffect(() => {
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  }, [selectedPeriod]);

  const handleExport = () => {
    if (!data || data.movements.length === 0) return;
    exportMovementsCsv(data);
  };

  const referenceMonthLabel = data?.referenceMonthLabel ?? '';

  const effectivePeriod =
    selectedPeriod === ALL_PERIOD_SELECTION ||
    availableCompetences.includes(selectedPeriod)
      ? selectedPeriod
      : ALL_PERIOD_SELECTION;

  const handlePeriodChange = (event: SelectChangeEvent<string>) => {
    onPeriodChange(event.target.value);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="fund-movements-period-label">Competência</InputLabel>
          <Select
            labelId="fund-movements-period-label"
            id="fund-movements-period-select"
            value={effectivePeriod}
            label="Competência"
            onChange={handlePeriodChange}
            disabled={isLoading}>
            <MenuItem value={ALL_PERIOD_SELECTION}>Todo período</MenuItem>
            {availableCompetences.map((competence) => (
              <MenuItem key={competence} value={competence}>
                {formatCompetenceOption(competence)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={handleExport}
          disabled={isLoading || !data || movements.length === 0}
          size="small">
          Exportar
        </Button>
      </Box>

      {isError ? (
        <Typography color="error" sx={{ py: 4, textAlign: 'center' }}>
          {errorMessage}
        </Typography>
      ) : (
        <>
          <SummaryCards summary={summary} showShares={true} />

          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.5,
              }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {referenceMonthLabel
                  ? `Histórico de movimentações - ${referenceMonthLabel}`
                  : 'Histórico de movimentações'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {`Total de ${movements.length} registro${movements.length === 1 ? '' : 's'}`}
              </Typography>
            </Box>

            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 200,
                }}>
                <CircularProgress size={36} />
              </Box>
            ) : (
              <MaterialReactTable table={table} />
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
